import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, guestEmailParams } from '../apiClient';

const ConfirmOrderPage = () => {
  const { mockupId } = useParams();
  const navigate = useNavigate();
  const [mockup, setMockup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmingOrder, setConfirmingOrder] = useState(false);
  const [orderMessage, setOrderMessage] = useState('');
  const [shippingEmail, setShippingEmail] = useState('');
  const [shippingPhone, setShippingPhone] = useState('');

  useEffect(() => {
    const fetchMockup = async () => {
      try {
        const response = await api.get(`/mockups/${mockupId}`, { params: guestEmailParams() });
        setMockup(response.data);
        // Initialize editable fields from fetched mockup data
        setShippingEmail(response.data.shipping_address?.email || '');
        setShippingPhone(response.data.shipping_address?.phone || '');
      } catch (err) {
        setError('Failed to load mockup details.');
        console.error('Error fetching mockup:', err);
      } finally {
        setLoading(false);
      }
    };

    if (mockupId) {
      fetchMockup();
    }
  }, [mockupId]);

  const handleConfirmOrder = async () => {
    if (!mockup || !mockup.shipping_address) return;

    setConfirmingOrder(true);
    setOrderMessage('');

    try {
      const response = await api.post(`/confirm-order/${mockup.order_id}`, null, {
        params: guestEmailParams(),
      });
      setOrderMessage(`Order confirmed! Order ID: ${response.data.order_id}`);
      // Optionally navigate to a success page or gallery
      // navigate('/gallery');
    } catch (err) {
      console.error('Error confirming order:', err);
      setOrderMessage(`Failed to confirm order: ${err.response?.data?.detail || err.message}`);
    } finally {
      setConfirmingOrder(false);
    }
  };

  if (loading) {
    return <div className="text-center mt-8">Loading order details...</div>;
  }

  if (error) {
    return <div className="text-center mt-8 text-red-500">Error: {error}</div>;
  }

  if (!mockup) {
    return <div className="text-center mt-8">Mockup not found.</div>;
  }

  return (
    <div className="container mx-auto p-4 bg-background rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-center">Confirm Your Order</h2>
      <div className="bg-card rounded-lg shadow-lg p-6 mb-6">
        <div className="flex justify-center mb-4">
          <img src={mockup.mockup_image_url} alt="Mockup" className="w-64 h-64 object-contain border border-gray-200 rounded-md shadow-sm" />
        </div>
        <p className="text-gray-700 mb-2"><strong>Prompt:</strong> {mockup.prompt}</p>
        <p className="text-gray-700 mb-2"><strong>Style:</strong> {mockup.style}</p>
        <p className="text-gray-700 mb-2"><strong>T-shirt Model:</strong> {mockup.tshirt_model_name}</p>
        <p className="text-gray-700 mb-2"><strong>Variant:</strong> {mockup.tshirt_variant_id}</p>
        {mockup.image_placement && <p className="text-gray-700 mb-2"><strong>Placement:</strong> {mockup.image_placement}</p>}
        {mockup.stitch_color && <p className="text-gray-700 mb-2"><strong>Stitch Color:</strong> {mockup.stitch_color}</p>}

        <h3 className="text-lg font-semibold mt-4 mb-2 text-gray-800">Shipping Address</h3>
        {mockup.shipping_address ? (
          <>
            <p className="text-gray-700 mb-1"><strong>Name:</strong> {mockup.shipping_address.first_name} {mockup.shipping_address.last_name}</p>
            <p className="text-gray-700 mb-1"><strong>Address:</strong> {mockup.shipping_address.address1}</p>
            <p className="text-gray-700 mb-1"><strong>City:</strong> {mockup.shipping_address.city}</p>
            <p className="text-gray-700 mb-1"><strong>State:</strong> {mockup.shipping_address.state}</p>
            <p className="text-gray-700 mb-1"><strong>Zip:</strong> {mockup.shipping_address.zip}</p>
            <p className="text-gray-700 mb-1"><strong>Country:</strong> {mockup.shipping_address.country_code}</p>
          </>
        ) : (
          <p className="text-red-500">Shipping address is not available for this mockup.</p>
        )}

        <div className="mb-3 mt-4">
          <label htmlFor="shippingEmail" className="block text-sm font-medium text-gray-700">Email:</label>
          <input
            type="email"
            id="shippingEmail"
            className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
            value={shippingEmail}
            onChange={(e) => setShippingEmail(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label htmlFor="shippingPhone" className="block text-sm font-medium text-gray-700">Phone:</label>
          <input
            type="tel"
            id="shippingPhone"
            className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
            value={shippingPhone}
            onChange={(e) => setShippingPhone(e.target.value)}
          />
        </div>
      </div>

      <button
        onClick={handleConfirmOrder}
        className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 transition-colors text-lg"
        disabled={confirmingOrder || !mockup.shipping_address}
      >
        {confirmingOrder ? 'Confirming Order...' : 'Confirm Order'}
      </button>
      {orderMessage && <p className="mt-4 text-center text-sm text-gray-600">{orderMessage}</p>}
    </div>
  );
};

export default ConfirmOrderPage;
